import { ActionTree } from 'vuex';
import { State } from '../state';
import { ActionAugments, instance } from '../actions';
import { h } from 'vue';

export interface OverviewActions {
    ['getAllCredentials'](context: ActionAugments): Promise<void>;
    ['getAllOAuth'](context: ActionAugments): Promise<void>;
    ['startOAuth'](context: ActionAugments, data: Record<string, string>): Promise<void>;
    ['updateOAuth'](context: ActionAugments, data: Record<string, string>): Promise<void>;
    ['removeOAuth'](context: ActionAugments, oauth: string): Promise<void>;
    ['saveCredential'](context: ActionAugments, payload: Record<string, string>): Promise<void>;
    ['editCredential'](context: ActionAugments, payload: Record<string, string>): Promise<void>;
    ['removeCredential'](context: ActionAugments, credential: string): Promise<void>;
    ['authorizeCredential'](context: ActionAugments, credential: string): Promise<void>;
    ['getOAuth2CallbackUrl'](context: ActionAugments): Promise<string>;
    ['testCredential'](context: ActionAugments, payload: Record<string, string>): Promise<Record<string, any>>;
    ['getAllFlows'](context: ActionAugments): Promise<void>;
    ['getAllManualStartedLogs'](context: ActionAugments): Promise<void>;
    ['getManualStartedLogsThisMonth'](context: ActionAugments): Promise<void>;
    ['runManualFlow'](context: ActionAugments, payload: Record<string, any>): Promise<void>;
    ['removeFlow'](context: ActionAugments, flow: string): Promise<void>;
    ['duplicateFlow'](context: ActionAugments, flow: Record<string, any>): Promise<void>;
    ['saveSchedule'](context: ActionAugments, schedule: Record<string, any>): Promise<string>;
    ['removeSchedule'](context: ActionAugments, schedule: string): Promise<void>;
    ['getStatisticsForMonth'](context: ActionAugments, Date: Record<string, any>): Promise<void>;
    ['getLogs'](context: ActionAugments, obj: Record<string, any>): Promise<Record<string, any>>;
    ['getSubscription'](context: ActionAugments): Promise<void>;
    ['getAllEmails'](context: ActionAugments): Promise<Array<string> | boolean>;
    ['getAllEmailObjects'](context: ActionAugments): Promise<Array<string> | boolean>;
    ['saveEmail'](context: ActionAugments, payload: Record<string, string>): Promise<void>;
    ['saveEmailObject'](context: ActionAugments, payload: Array<string> | boolean): Promise<void>;
    ['verifyEmail'](context: ActionAugments, payload: Record<string, string>): Promise<void>;
    ['removeEmail'](context: ActionAugments, email: string): Promise<void>;
    ['getAllEndpoints'](context: ActionAugments): Promise<void>;
    ['requestEndpoint'](context: ActionAugments, payload: Record<string, any>): Promise<Record<string, any>>;
    ['removeEndpoint'](context: ActionAugments, flow: string): Promise<void>;
    ['getFlowsOrder'](context: ActionAugments): Promise<void>;
    ['saveFlowsOrder'](context: ActionAugments, order: string[]): Promise<void>;
    ['getSchedulesOrder'](context: ActionAugments): Promise<void>;
    ['saveSchedulesOrder'](context: ActionAugments, scheduledFlowsOrder: string[]): Promise<void>;
    ['getCredentialsOrder'](context: ActionAugments): Promise<void>;
    ['saveCredentialsOrder'](context: ActionAugments, credentialsOrder: Record<string, any> | null): Promise<void>;
    ['fetchWorkingHours'](context: ActionAugments): Promise<void>;
    ['updateWorkingHours'](context: ActionAugments, workingHours: { startTime: string; endTime: string }): Promise<void>;
    ['saveMultipleSchedules'](context: ActionAugments, scheduleData: Record<string, any>): Promise<void>;
    ['removeSpecificSchedule'](context: ActionAugments, payload: { flowId: string; scheduleId: string }): Promise<void>;
    ['getConnectorUsage'](context: ActionAugments, connectorName?: string): Promise<Array<{ flowId: string, name: string, getConnectorIdCount: number, updateConnectorIdCount: number }>>;
    ['getLastRuns'](context: ActionAugments): Promise<Array<Record<string, any>>>;
    ['getFuturePlannedDate'](context: ActionAugments, payload: Record<string, any>): Promise<{ plannedDate: string; plannedDateReadable: string }>;
    ['getFuturePlannedDates'](context: ActionAugments, payload: Record<string, any>): Promise<{ nextRuns: Array<{ runNumber: number; plannedDate: string; plannedDateReadable: string }> }>;
    ['getLicenseData'](context: ActionAugments): Promise<void>;
    ['getLicenseHistory'](context: ActionAugments, obj?: Record<string, any>): Promise<Array<Record<string, any>>>;
    ['updateLicense'](context: ActionAugments, licenseData: Record<string, any>): Promise<void>;
}

export const overviewActions: ActionTree<State, State> & OverviewActions = {
    ['getAllCredentials']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'credentials'
            }).then((res) => {
                commit('setAllCredentials', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllOAuth']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'auth/external'
            }).then((res) => {
                commit('setAllOAuth', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['startOAuth']({ commit }, data: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `auth/external`,
                data
            }).then((response) => {
                window.location.href = response.data;
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'OAuth starten mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['updateOAuth']({ commit }, data: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'PUT',
                url: `auth/external`,
                data
            }).then((response) => {
                window.location.href = response.data;
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'OAuth opnieuw starten mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeOAuth']({ commit }, oauth: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `auth/external/${oauth}`
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'OpenID Connect verwijderd',
                        meta: 'OAuth Deleted'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'OpenID Connect verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveCredential']({ commit }, payload: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            const data: Record<string, any> = {
                environment: payload.environment,
                type: payload.type
            };

            if (payload.clientId) data.clientId = payload.clientId;
            if (payload.clientSecret) data.clientSecret = payload.clientSecret;
            if (payload.template) data.template = payload.template;
            if (payload.authorizationUrl) data.authorizationUrl = payload.authorizationUrl;
            if (payload.tokenUrl) data.tokenUrl = payload.tokenUrl;
            if (payload.scope) data.scope = payload.scope;
            if (payload.value) data.value = payload.value;

            instance({
                method: 'POST',
                url: `credentials/${payload.name}`,
                data
            }).then(() => {
                if (payload.value) {
                    commit('notify', {
                        type: 'success',
                        options: {
                            title: 'Wachtwoord / Token aangemaakt',
                            meta: 'Credential Created'
                        }
                    });
                }
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Wachtwoord / Token aanmaken mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['editCredential']({ commit }, payload: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            const data: Record<string, any> = {
                name: payload.name,
                environment: payload.environment,
                type: payload.type
            };

            if (payload.clientId) data.clientId = payload.clientId;
            if (payload.clientSecret) data.clientSecret = payload.clientSecret;
            if (payload.template) data.template = payload.template;
            if (payload.authorizationUrl) data.authorizationUrl = payload.authorizationUrl;
            if (payload.tokenUrl) data.tokenUrl = payload.tokenUrl;
            if (payload.scope) data.scope = payload.scope;
            if (payload.value) data.value = payload.value;

            instance({
                method: 'PUT',
                url: `credentials/${payload.previousName}`,
                data
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Wachtwoord / Token aangepast',
                        meta: 'Credential Edited'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Wachtwoord / Token aanpassen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeCredential']({ commit }, credential: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `credentials/${credential}`
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Wachtwoord / Token verwijderd',
                        meta: 'Credential Deleted'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Wachtwoord / Token verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['authorizeCredential']({ commit }, credential: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: `credentials/${credential}/authorize`,
                params: { returnUrl: window.location.href }
            }).then((res) => {
                window.location.href = res.data.url;
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Autoriseren mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getOAuth2CallbackUrl']({ commit }): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            instance({
                method: 'GET',
                url: 'oauth2/config'
            }).then((res) => {
                resolve(res.data.callbackUrl);
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Ophalen callback URI mislukt',
                        meta: e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            });
        });
    },
    ['testCredential']({ commit }, payload: Record<string, string>): Promise<Record<string, any>> {
        return new Promise<Record<string, any>>((resolve, reject) => {
            commit('setLoading', true);
            const data: Record<string, any> = {
                environment: payload.environment
            };
            if (payload.type === 'afas_oauth_token') {
                data.type = payload.type;
                if (payload.clientId) data.clientId = payload.clientId;
                if (payload.clientSecret) data.clientSecret = payload.clientSecret;
            } else {
                if (payload.value) data.value = payload.value;
            }
            instance({
                method: 'POST',
                url: `credentials/${payload.name}/test`,
                data
            }).then(({ data: metainfo }) => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Token getest',
                        description: () => h('div', [
                            h('b', `${metainfo.info.appName}`),
                            h('br'),
                            h('span', 'GetConnectoren:'),
                            h('br'),
                            h('div', { style: { padding: '0 25px'}}, [
                                h('span', `${metainfo.getConnectors?.map((x: any) => `"${x.id}"`).join(`, `) || "Geen"}`)
                            ]),
                            h('span', 'UpdateConnectoren:'),
                            h('br'),
                            h('div', { style: { padding: '0 25px'}}, [
                                h('span', `${metainfo.updateConnectors?.map((x: any) => `"${x.id}"`).join(`, `) || "Geen"}`)
                            ]),
                        ]),
                        meta: 'Credential Tested'
                    }
                });
                resolve(metainfo);
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Token testen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllFlows']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'flows'
            }).then((res) => {
                commit('setAllFlows', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllManualStartedLogs']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'logs/manual',
            }).then((res) => {
                commit('setAllManualStartedLogs', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getManualStartedLogsThisMonth']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'logs/manualthismonth',
            }).then((res) => {
                commit('setManualStartedLogsThisMonth', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['runManualFlow']({ commit }, options: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `manualrun`,
                data: {
                    flow: options.flow,
                    type: options.type
                }
            }).then((res) => {
                resolve(res.data);
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Succesvol ingepland',
                        meta: 'Manual Run Scheduled'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Fout tijdens starten',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getSubscription']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'organizations/subscription'
            }).then((res) => {
                commit('setSubscription', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeFlow']({ commit }, flow: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `flows/${flow}`
            }).then(() => {
                resolve();
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Flow verwijderd',
                        meta: 'Flow Deleted'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Flow verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['duplicateFlow']({ commit }, flow: Record<string, any>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `flows/${flow.id}/duplicate`,
                data: {
                    name: flow.name
                }
            }).then(() => {
                resolve();
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Flow gedupliceerd',
                        meta: 'Flow Duplicated'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Flow dupliceren mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveSchedule']({ commit }, schedule: Record<string, any>): Promise<string> {
        const payload = JSON.parse(JSON.stringify(schedule));
        delete payload.flow;
        return new Promise<string>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `schedules/${schedule.flow}`,
                data: payload
            }).then((res) => {
                resolve(res.data);
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Plan opgeslagen',
                        meta: 'Schedule Saved'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Plan opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeSchedule']({ commit }, schedule: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `schedules/${schedule}`
            }).then(() => {
                resolve();
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Plan verwijderd',
                        meta: 'Schedule Deleted'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Plan verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getStatisticsForMonth']({ commit }, { year, month }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'GET',
                url: `statistics?year=${year}&month=${month}`
            }).then((res) => {
                commit('setStatistics', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            });
        });
    },
    ['getLogs']({ commit }, obj: Record<string, any>): Promise<Record<string, any>> {
        return new Promise<Record<string, any>>((resolve, reject) => {
            commit('setLoading', true);
            let url = `flows/${obj.flow}/logs`;
            const query: string[] = [];
            if (obj.skip !== undefined && obj.skip !== null) {
                query.push(`skip=${obj.skip}`);
            }
            if (obj.take !== undefined && obj.take !== null) {
                query.push(`take=${obj.take}`);
            }
            if (query.length > 0) {
                url += `?${query.join('&')}`;
            }
            instance({
                method: 'GET',
                url: url
            }).then((res) => {
                resolve(res.data);
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllEmails']({ commit }): Promise<Array<string> | boolean> {
        return new Promise<Array<string> | boolean>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'emails'
            }).then((res) => {
                commit('setAllEmails', res.data);
                resolve(res.data);
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllEmailObjects']({ commit }): Promise<Array<string> | boolean> {
        return new Promise<Array<string> | boolean>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'emails/object'
            }).then((res) => {
                commit('setAllEmailObjects', res.data);
                resolve(res.data);
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveEmail']({ commit }, payload: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `emails`,
                data: {
                    email: payload.email
                }
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Verificatie code verstuurd',
                        meta: 'Verficiation Code Send'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Verificatie code versturen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveEmailObject']({ commit }, payload: Array<string> | boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `emails/object`,
                data: {
                    emails: payload
                }
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Rapport interval ingesteld',
                        meta: 'Rapport interval set'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Rapport interval instellen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['verifyEmail']({ commit }, payload: Record<string, string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: `emails/verify`,
                data: {
                    email: payload.email,
                    verificationCode: payload.verificationCode
                }
            }).then(() => {
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Email geverifieerd',
                        meta: 'Email Verified'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Email verifieren mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeEmail']({ commit }, email: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `emails/${email}`
            }).then(() => {
                resolve();
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Email verwijderd',
                        meta: 'Email Deleted'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Email verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getAllEndpoints']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'endpoints'
            }).then((res) => {
                commit('setAllEndpoints', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['requestEndpoint']({ commit }, payload: { flow: string, endpointSource?: 'afas' | 'external-async' | 'external-sync', runtime?: '1' | '5' | '30' | '120' }): Promise<Record<string, any>> {
        return new Promise<Record<string, any>>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: 'endpoints',
                data: { flow: payload.flow, endpointSource: payload.endpointSource, runtime: payload.runtime }
            }).then((res) => {
                resolve(res.data);
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Endpoint aangemaakt',
                        meta: 'Sla de API key op, deze wordt niet meer getoond'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Endpoint aanmaken mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['removeEndpoint']({ commit }, flow: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: 'endpoints',
                data: { flow }
            }).then(() => {
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Endpoint verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getFlowsOrder']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'GET',
                url: 'flows/order'
            }).then((res) => {
                commit('setFlowsOrder', res.data);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Flows order ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveFlowsOrder']({ commit }, order: string[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'POST',
                url: 'flows/order',
                data: { order }
            }).then(() => {
                commit('setFlowsOrder', order);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Flows order opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getSchedulesOrder']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'GET',
                url: 'flows/schedules/order'
            }).then((res) => {
                commit('setSchedulesOrder', res.data.scheduledFlowsOrder);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Schedules order ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveSchedulesOrder']({ commit }, scheduledFlowsOrder: string[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'POST',
                url: 'flows/schedules/order',
                data: { scheduledFlowsOrder }
            }).then(() => {
                commit('setSchedulesOrder', scheduledFlowsOrder);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Schedules order opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getCredentialsOrder']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'GET',
                url: 'flows/credentials/order'
            }).then((res) => {
                commit('setCredentialsOrder', res.data.credentialsOrder);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Credentials order ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },    
    ['saveCredentialsOrder']({ commit }, credentialsOrder: string[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'POST',
                url: 'flows/credentials/order',
                data: { credentialsOrder }
            }).then(() => {
                commit('setCredentialsOrder', credentialsOrder);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Credentials order opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['fetchWorkingHours']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'GET',
                url: 'flows/workinghours'
            }).then((res) => {
                commit('setWorkingHours', res.data);
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Working hours ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['updateWorkingHours']({ commit }, workingHours: { startTime: string; endTime: string }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            instance({
                method: 'POST',
                url: 'flows/workinghours',
                data: workingHours
            }).then(() => {
                commit('setWorkingHours', workingHours);
                commit('notify', { 
                    type: 'success',
                    options: {
                        title: 'Werktijden opgeslagen',
                        meta: 'Working hours saved successfully'
                    }
                });
                resolve();
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Werktijden opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['saveMultipleSchedules']({ commit }, scheduleData): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            const payload = JSON.parse(JSON.stringify(scheduleData));
            delete payload.flow;
            delete payload.isEdit;
            delete payload.scheduleId;

            instance({
                method: scheduleData.isEdit && scheduleData.scheduleId ? 'PUT' : 'POST',
                url: scheduleData.isEdit && scheduleData.scheduleId 
                    ? `schedules/${scheduleData.flow}/schedules/${scheduleData.scheduleId}` 
                    : `schedules/${scheduleData.flow}/schedules`,
                data: scheduleData.isEdit && scheduleData.scheduleId ? payload : [payload]
            }).then((res) => {
                resolve(res.data);
                commit('notify', {
                    type: 'success',
                    options: {
                        title: scheduleData.isEdit ? 'Planning bijgewerkt' : 'Planning opgeslagen',
                        meta: scheduleData.isEdit ? 'Schedule Updated' : 'Schedule Saved'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: scheduleData.isEdit ? 'Planning bijwerken mislukt' : 'Planning opslaan mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },

    ['removeSpecificSchedule']({ commit }, { flowId, scheduleId }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'DELETE',
                url: `schedules/${flowId}/schedules/${scheduleId}`
            }).then(() => {
                resolve();
                commit('notify', {
                    type: 'success',
                    options: {
                        title: 'Planning verwijderd',
                        meta: 'Schedule Deleted'
                    }
                });
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Planning verwijderen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getConnectorUsage']({ commit }, connectorName?: string): Promise<Array<{ flowId: string, name: string, getConnectorIdCount: number, updateConnectorIdCount: number }>> {
        return new Promise((resolve, reject) => {
            commit('setLoading', true);
            console.log('[getConnectorUsage] Fetching connector usage for:', connectorName);
            instance({
                method: 'GET',
                url: connectorName
                    ? `credentials/usage/connectors?connector=${encodeURIComponent(connectorName)}`
                    : 'credentials/usage/connectors'
            }).then((res) => {
                console.log('[getConnectorUsage] Response:', res.data);
                resolve(res.data);
            }).catch((e) => {
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Connector usage ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getLastRuns']: async ({ commit }): Promise<Array<Record<string, any>>> => {
        commit('setLoading', true);
        try {
            const res = await instance({
                method: 'GET',
                url: `statistics/lastruns`
            });
            commit('setLastRuns', res.data.logs);
            commit('setRunningFlows', res.data.runningFlows);
            commit('setQueuedFlows', res.data.queuedFlows);
            return res.data.logs;
        } catch (e: any) {
            commit('notify', {
                type: 'error',
                options: {
                    title: 'Laatst uitgevoerde runs ophalen mislukt',
                    meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                }
            });
            throw e;
        } finally {
            commit('setLoading', false);
        }
    },
    ['getFuturePlannedDate']({ commit }, payload: Record<string, any>): Promise<{ plannedDate: string; plannedDateReadable: string }> {
        return new Promise((resolve, reject) => {
            console.log('Dispatching getFuturePlannedDate with payload:', payload);
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: 'schedules/future-planned-date',
                data: payload
            }).then((res) => {
                console.log('Received future planned date:', res.data);
                commit('setFuturePlannedDate', res.data);
                resolve(res.data);
            }).catch((e) => {
                console.error('Error fetching future planned date:', e);
                commit('setFuturePlannedDate', null);
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Planned date ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getFuturePlannedDates']({ commit }, payload: Record<string, any>): Promise<{ nextRuns: Array<{ runNumber: number; plannedDate: string; plannedDateReadable: string }> }> {
        return new Promise((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'POST',
                url: 'schedules/future-planned-dates',
                data: payload
            })
                .then((res) => {
                    // Verwacht: res.data.nextRuns (array)
                    commit('setFuturePlannedDates', { nextRuns: res.data.nextRuns });
                    resolve({ nextRuns: res.data.nextRuns });
                })
                .catch((e) => {
                    commit('setFuturePlannedDates', null);
                    commit('notify', {
                        type: 'error',
                        options: {
                            title: 'Volgende geplande uitvoeringen ophalen mislukt',
                            meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                        }
                    });
                    reject(e);
                })
                .finally(() => {
                    commit('setLoading', false);
                });
        });
    },
    ['getLicenseData']({ commit }): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            commit('setLoading', true);
            instance({
                method: 'GET',
                url: 'license'
            }).then((res) => {
                commit('setLicenseData', res.data);
                resolve();
            }).catch((e) => {
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['getLicenseHistory']({ commit }, obj: Record<string, any> = {}): Promise<Array<Record<string, any>>> {
        return new Promise<Array<Record<string, any>>>((resolve, reject) => {
            commit('setLoading', true);
            const skip = Number(obj.skip);
            const take = Number(obj.take);
            const normalizedTake = Number.isFinite(take) ? Math.min(1000, Math.max(1, Math.floor(take))) : 1000;
            let currentSkip = Number.isFinite(skip) ? Math.max(0, Math.floor(skip)) : 0;
            const allHistory: Array<Record<string, any>> = [];

            const fetchNextPage = (): Promise<void> => {
                return instance({
                    method: 'GET',
                    url: 'license/history',
                    params: {
                        skip: currentSkip,
                        take: normalizedTake
                    }
                }).then((res) => {
                    const batch = Array.isArray(res.data) ? res.data : [];
                    allHistory.push(...batch);

                    if (batch.length === normalizedTake) {
                        currentSkip += normalizedTake;
                        return fetchNextPage();
                    }
                });
            };

            fetchNextPage().then(() => {
                commit('setLicenseHistory', allHistory);
                resolve(allHistory);
            }).catch((e) => {
                commit('setLicenseHistory', []);
                commit('notify', {
                    type: 'error',
                    options: {
                        title: 'Licentiehistorie ophalen mislukt',
                        meta:  e.response?.data?.error || e.response?.data || e.message || 'Internal Server Error'
                    }
                });
                reject(e);
            }).finally(() => {
                commit('setLoading', false);
            });
        });
    },
    ['updateLicense']: async ({ commit }, licenseData: Record<string, any>): Promise<void> => {
        commit('setLoading', true);
        try {
            const res = await instance({
                method: 'POST',
                url: 'license',
                data: licenseData
            });
            commit('setLicenseData', res.data);
            commit('notify', {
                type: 'success',
                options: {
                    title: 'Licentie bijgewerkt',
                    meta: 'License Updated'
                }
            });
        } catch (e: any) {
            commit('setAlert', {
                type: 'danger',
                title: 'Licentie bijwerken mislukt',
                text: e.response?.data || e.message || 'Internal Server Error'
            });
            throw e;
        } finally {
            commit('setLoading', false);
        }
    },
};